import {defineStore} from 'pinia';
import {syncRefs, useLocalStorage} from '@vueuse/core';
import {computed, watch} from 'vue';
import {
    currentLocaleStorageKey,
    getAvailableLanguages,
    getCurrentLocale,
    getLocale,
    isValidLocale,
} from '@deskly/shared-ui/types';

export {getAvailableLanguages, getCurrentLocale};

export function toDayjsLocale(locale: string): string {
    return locale === 'en' ? 'en-gb' : locale;
}

export const useLocaleStore = defineStore('locale', () => {
    /**
     * The locale which is set within local storage.
     * Can potentially not be contained within `availableLanguages`.
     */
    const storageLocale = useLocalStorage<string | null>(currentLocaleStorageKey, null);

    /**
     * The current, valid locale.
     */
    const currentLocale = computed(() => getLocale(storageLocale.value));

    syncRefs(currentLocale, storageLocale);

    /**
     * Update document language esp. for screen readers
     */
    watch(
        currentLocale,
        newLocale => {
            document.documentElement.lang = newLocale;
        },
        {immediate: true}
    );

    /**
     * Sets the new target locale, if it is found
     * within the available languages.
     * @param locale Target locale.
     */
    function updateLocale(locale: string) {
        if (!isValidLocale(locale)) {
            return;
        }

        storageLocale.value = locale;
    }

    /**
     * Languages for which translations are available
     */
    const availableLanguages = getAvailableLanguages();

    const supportedLocale = computed(() =>
        ['en', 'de'].includes(currentLocale.value) ? currentLocale.value : 'en'
    );

    const dayjsLocale = computed(() => toDayjsLocale(currentLocale.value));

    return {
        currentLocale,
        supportedLocale,
        storageLocale,
        availableLanguages,
        updateLocale,
        dayjsLocale,
    };
});
